const initK9ContactForm = (root = document) => {
  const form = root.querySelector?.('#K9ContactForm');

  if (!form || form.dataset.k9ValidationInitialized === 'true') return;

  form.dataset.k9ValidationInitialized = 'true';

  const fields = Array.from(form.querySelectorAll('[required]'));
  const email = form.querySelector('input[name="contact[email]"]');
  const confirmation = form.querySelector('[data-k9-email-confirmation]');
  const postalCode = form.querySelector('[data-k9-postal-code]');
  const prefecture = form.querySelector('[data-k9-prefecture]');
  const address = form.querySelector('[data-k9-address]');
  const postalStatus = form.querySelector('[data-k9-postal-status]');
  const emailFormatError = form.querySelector('[data-k9-email-format-error]');
  const submitButton = form.querySelector('button[type="submit"]');
  const errors = new Map();
  const requiredMessages = {
    'contact[お問い合わせ件名]': 'お問い合わせ件名を選択してください。',
    'contact[body]': 'お問い合わせ内容を入力してください。',
    'contact[姓]': '姓を入力してください。',
    'contact[名]': '名を入力してください。',
    'contact[姓（かな）]': '姓（かな）を入力してください。',
    'contact[名（かな）]': '名（かな）を入力してください。',
    'contact[郵便番号]': '郵便番号を入力してください。',
    'contact[都道府県]': '都道府県を入力してください。',
    'contact[市区町村・番地]': '市区町村・番地を入力してください。',
    'contact[email]': 'メールアドレスを入力してください。',
    'contact[メールアドレス（確認用）]': '確認用メールアドレスを入力してください。',
    'contact[phone]': '電話番号を入力してください。',
  };

  fields.forEach((field, index) => {
    let error = null;

    if (field === email) {
      error = emailFormatError;
    } else if (field === confirmation) {
      error = form.querySelector('[data-k9-email-error]');
    }

    if (!error) {
      error = document.createElement('p');
      error.className = 'k9-contact-form__field-error';
      error.hidden = true;

      const nameLabel = field.closest('.k9-contact-form__name-grid label');

      if (nameLabel) {
        nameLabel.append(error);
      } else {
        field.insertAdjacentElement('afterend', error);
      }
    }

    if (!field.id) field.id = `K9ContactField-${index + 1}`;

    error.id ||= `${field.id}-error`;
    error.setAttribute('aria-live', 'polite');

    const describedBy = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    describedBy.add(error.id);
    field.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
    errors.set(field, error);
  });

  const updateEmailConfirmationValidity = () => {
    if (!email || !confirmation) return;

    const mismatches = confirmation.value.length > 0 && email.value !== confirmation.value;
    confirmation.setCustomValidity(mismatches ? 'メールアドレスが一致していません。' : '');
  };

  const normalizePostalCode = (value) =>
    value
      .replace(/[０-９]/g, (character) => String.fromCharCode(character.charCodeAt(0) - 0xfee0))
      .replace(/\D/g, '');

  const setPostalStatus = (message = '', isError = false) => {
    if (!postalStatus) return;

    postalStatus.textContent = message;
    postalStatus.hidden = message.length === 0;
    postalStatus.classList.toggle('is-error', isError);
  };

  let postalLookupTimer;
  let postalLookupController;
  let lastSearchedPostalCode = '';

  const lookupPostalAddress = async () => {
    if (!postalCode || !prefecture || !address) return;

    const normalizedPostalCode = normalizePostalCode(postalCode.value);

    if (normalizedPostalCode.length !== 7) {
      lastSearchedPostalCode = '';
      setPostalStatus();
      return;
    }

    if (normalizedPostalCode === lastSearchedPostalCode) return;

    lastSearchedPostalCode = normalizedPostalCode;
    postalCode.value = `${normalizedPostalCode.slice(0, 3)}-${normalizedPostalCode.slice(3)}`;
    postalLookupController?.abort();
    postalLookupController = new AbortController();
    setPostalStatus('住所を検索しています。');

    try {
      const response = await fetch(
        `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${encodeURIComponent(normalizedPostalCode)}`,
        { signal: postalLookupController.signal }
      );

      if (!response.ok) throw new Error(`ZipCloud request failed: ${response.status}`);

      const data = await response.json();
      const result = Array.isArray(data.results) ? data.results[0] : null;

      if (!result) {
        setPostalStatus('該当する住所が見つかりませんでした。手入力してください。', true);
        return;
      }

      prefecture.value = result.address1 || '';
      address.value = `${result.address2 || ''}${result.address3 || ''}`;
      prefecture.dispatchEvent(new Event('input', { bubbles: true }));
      address.dispatchEvent(new Event('input', { bubbles: true }));
      setPostalStatus('住所を自動入力しました。番地以降をご確認ください。');
    } catch (error) {
      if (error.name === 'AbortError') return;

      setPostalStatus('住所を取得できませんでした。手入力してください。', true);
    }
  };

  const getErrorMessage = (field) => {
    if (field.validity.valueMissing) {
      return requiredMessages[field.name] || 'この項目を入力してください。';
    }

    if (field.validity.typeMismatch && field.type === 'email') {
      return '正しいメールアドレスを入力してください。';
    }

    if (field === confirmation && field.validity.customError) {
      return 'メールアドレスが一致していません。';
    }

    return field.validity.valid ? '' : '入力内容をご確認ください。';
  };

  const updateFieldError = (field, revealError = false) => {
    const error = errors.get(field);

    if (!error) return;

    if (field === confirmation) updateEmailConfirmationValidity();

    const message = getErrorMessage(field);
    const shouldShow = revealError && message.length > 0;

    error.textContent = message;
    error.hidden = !shouldShow;

    if (shouldShow) {
      field.setAttribute('aria-invalid', 'true');
    } else {
      field.removeAttribute('aria-invalid');
    }
  };

  fields.forEach((field) => {
    field.addEventListener('blur', () => updateFieldError(field, true));
    field.addEventListener('change', () => updateFieldError(field, true));
    field.addEventListener('input', () => {
      const revealError = !errors.get(field)?.hidden;
      updateFieldError(field, revealError);

      if (field === email && confirmation) {
        updateFieldError(confirmation, !errors.get(confirmation)?.hidden);
      }
    });
  });

  if (postalCode) {
    postalCode.addEventListener('input', () => {
      window.clearTimeout(postalLookupTimer);

      if (normalizePostalCode(postalCode.value).length !== 7) {
        lastSearchedPostalCode = '';
        postalLookupController?.abort();
        setPostalStatus();
        return;
      }

      postalLookupTimer = window.setTimeout(lookupPostalAddress, 350);
    });

    postalCode.addEventListener('blur', () => {
      window.clearTimeout(postalLookupTimer);
      lookupPostalAddress();
    });
  }

  // JSが無効な場合はHTML標準の必須チェックを維持し、有効な場合だけ独自表示へ切り替える。
  form.noValidate = true;

  const validateForm = () => {
    updateEmailConfirmationValidity();
    fields.forEach((field) => updateFieldError(field, true));

    return fields.find((field) => !field.validity.valid);
  };

  const blockInvalidSubmission = (event) => {
    const firstInvalidField = validateForm();

    if (!firstInvalidField) return;

    event.preventDefault();
    // ShopifyのreCAPTCHAが無効なフォームを後から再送信しないよう、
    // 検証エラー時は後続のsubmitハンドラーへ処理を渡さない。
    event.stopImmediatePropagation();
    firstInvalidField.focus();
  };

  // submitイベントより先にクリックを止め、ShopifyのreCAPTCHAによる遅延再送信も防ぐ。
  submitButton?.addEventListener('click', blockInvalidSubmission, { capture: true });
  form.addEventListener('submit', blockInvalidSubmission, { capture: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initK9ContactForm());
} else {
  initK9ContactForm();
}

document.addEventListener('shopify:section:load', (event) => initK9ContactForm(event.target));
